/**
 * FloatingText — a small pool of rising, fading text labels used for mining
 * "damage" ticks (+N minerals), XP gains (+N XP), and level-up banners. Labels
 * live in world space (added to the world container) so they track the camera.
 */
import { Container, Text } from "pixi.js";

interface Floater {
  t: Text;
  life: number;
  max: number;
  vy: number; // world units/sec (upward = negative)
}

export interface FloaterOpts {
  size?: number;
  life?: number;
  vy?: number;
}

export class FloatingText {
  readonly view = new Container();
  private pool: Text[] = [];
  private active: Floater[] = [];

  spawn(
    x: number,
    y: number,
    label: string,
    color: number,
    opts: FloaterOpts = {},
  ): void {
    if (this.active.length > 48) return; // hard cap
    const size = opts.size ?? 14;
    const life = opts.life ?? 0.9;
    const vy = opts.vy ?? -34;

    let t = this.pool.pop();
    if (!t) {
      t = new Text({
        text: label,
        style: {
          fontFamily: "system-ui, Arial, sans-serif",
          fontSize: size,
          fontWeight: "700",
          fill: color,
          stroke: { color: 0x05070f, width: 3 },
        },
      });
      t.anchor.set(0.5);
    } else {
      t.text = label;
      t.style.fill = color;
      t.style.fontSize = size;
    }
    t.position.set(x, y);
    t.alpha = 1;
    t.visible = true;
    this.view.addChild(t);
    this.active.push({ t, life, max: life, vy });
  }

  update(dt: number): void {
    for (let i = this.active.length - 1; i >= 0; i--) {
      const f = this.active[i]!;
      f.life -= dt;
      f.t.y += f.vy * dt;
      const k = f.life / f.max; // 1 → 0
      f.t.alpha = Math.max(0, Math.min(1, k * 1.4));
      if (f.life <= 0) {
        f.t.visible = false;
        this.view.removeChild(f.t);
        this.pool.push(f.t);
        this.active.splice(i, 1);
      }
    }
  }

  clear(): void {
    for (const f of this.active) {
      f.t.visible = false;
      this.view.removeChild(f.t);
      this.pool.push(f.t);
    }
    this.active.length = 0;
  }
}
